// @ts-nocheck
import { useState, useEffect } from 'react'
import { useApi } from '../hooks/useApi'
import { Plus, Pencil, Trash2, ListTree, Loader2 } from 'lucide-react'
import IvrMenuEditor from './IvrMenuEditor'

interface IvrMenuPrompt {
  type: 'tts' | 'audio_url'
  ttsText?: string
  ttsLanguage?: string
  ttsVoice?: string
  audioUrl?: string
}

interface IvrMenuOption {
  label: string
  action: 'play_message' | 'go_to_ivr' | 'route_to_group' | 'route_to_queue'
  message?: IvrMenuPrompt
  afterMessage?: 'hangup' | 'return'
  targetIvrMenuId?: string
  groupId?: string
}

interface IvrMenu {
  ivrMenuId: string
  organizationId: string
  name: string
  prompt: IvrMenuPrompt
  options: Record<string, IvrMenuOption>
  timeoutAction?: 'replay' | 'hangup'
  invalidAction?: 'replay' | 'hangup'
  maxAttempts?: number
  createdAt: number
  updatedAt: number
}

interface Group {
  groupId: string
  groupName: string
}

/**
 * IVRメニュー管理コンポーネント
 * IVRメニューの一覧表示・作成・編集・削除を行う
 */
export default function IvrMenusSettings() {
  const api = useApi()
  const [menus, setMenus] = useState<IvrMenu[]>([])
  const [groups, setGroups] = useState<Group[]>([])
  const [loading, setLoading] = useState(true)
  const [editingMenu, setEditingMenu] = useState<IvrMenu | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)

  // IVRメニュー一覧とグループ一覧を取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [menusData, groupsData] = await Promise.all([
          api.get('/api/ivr-menus'),
          api.get('/api/groups'),
        ])
        setMenus(menusData.items || [])
        setGroups(groupsData.items || [])
      } catch (err) {
        console.error('IVRメニュー取得エラー:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [api])

  // プロンプトのプレビューテキストを取得
  const getPromptPreview = (menu: IvrMenu): string => {
    if (!menu.prompt) return '未設定'
    if (menu.prompt.type === 'audio_url') {
      return '音声ファイル'
    }
    if (menu.prompt.ttsText) {
      return menu.prompt.ttsText.length > 60
        ? menu.prompt.ttsText.substring(0, 60) + '...'
        : menu.prompt.ttsText
    }
    return '未設定'
  }

  // オプション数の表示テキスト
  const getOptionsCount = (menu: IvrMenu): string => {
    const count = menu.options ? Object.keys(menu.options).length : 0
    return `${count}件のオプション`
  }

  // メニュー保存（作成・更新共通）
  const handleSave = async (menuData: Partial<IvrMenu>) => {
    if (editingMenu) {
      // 更新
      const updated = await api.put(`/api/ivr-menus/${editingMenu.ivrMenuId}`, menuData)
      setMenus(menus.map(m => m.ivrMenuId === editingMenu.ivrMenuId ? updated : m))
    } else {
      // 新規作成
      const created = await api.post('/api/ivr-menus', menuData)
      setMenus([...menus, created])
    }
    setEditingMenu(null)
    setIsCreating(false)
  }

  // メニュー削除
  const handleDelete = async (ivrMenuId: string) => {
    if (!confirm('このIVRメニューを削除しますか？')) return

    try {
      setDeleting(ivrMenuId)
      await api.delete(`/api/ivr-menus/${ivrMenuId}`)
      setMenus(menus.filter(m => m.ivrMenuId !== ivrMenuId))
    } catch (err) {
      if (err.status === 409) {
        alert('このIVRメニューは電話番号に設定されているため削除できません。先に電話番号の設定を変更してください。')
      } else {
        alert(`削除エラー: ${err.message}`)
      }
    } finally {
      setDeleting(null)
    }
  }

  // エディターを閉じる
  const handleCloseEditor = () => {
    setEditingMenu(null)
    setIsCreating(false)
  }

  return (
    <div className="space-y-6">
      {/* エディターモーダル */}
      {(isCreating || editingMenu) && (
        <IvrMenuEditor
          menu={editingMenu}
          groups={groups}
          allMenus={menus}
          onSave={handleSave}
          onClose={handleCloseEditor}
        />
      )}

      {/* メニュー一覧 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        {/* ヘッダー */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                <ListTree className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">IVRメニュー管理</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">自動音声応答メニューの作成・管理</p>
              </div>
            </div>
            <button
              onClick={() => setIsCreating(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              新規作成
            </button>
          </div>
        </div>

        {/* リスト */}
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <div className="p-8 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>読み込み中...</span>
            </div>
          ) : menus.length === 0 ? (
            <div className="p-12 text-center">
              <ListTree className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">IVRメニューはまだありません</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">「新規作成」ボタンからIVRメニューを追加してください</p>
            </div>
          ) : (
            menus.map(menu => (
              <div
                key={menu.ivrMenuId}
                className="p-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                {/* アイコン */}
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg flex items-center justify-center">
                  <ListTree className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
                </div>

                {/* メニュー情報 */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white">{menu.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {getPromptPreview(menu)}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {getOptionsCount(menu)}
                  </p>
                </div>

                {/* アクション */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setEditingMenu(menu)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="編集"
                  >
                    <Pencil className="w-4 h-4 text-gray-500" />
                  </button>
                  <button
                    onClick={() => handleDelete(menu.ivrMenuId)}
                    disabled={deleting === menu.ivrMenuId}
                    className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                    title="削除"
                  >
                    {deleting === menu.ivrMenuId ? (
                      <Loader2 className="w-4 h-4 text-red-500 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 text-red-500" />
                    )}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
